import { useEffect, useRef } from 'react';

const MANIFEST_URL = `${process.env.PUBLIC_URL || ''}/asset-manifest.json`;
const DEFAULT_POLL_INTERVAL = 60_000;
const MANIFEST_TIMEOUT = 8_000;

const fetchManifestSignature = async () => {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return null;
  }
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), MANIFEST_TIMEOUT);
  try {
    const response = await fetch(MANIFEST_URL, {
      cache: 'no-store',
      headers: { 'Cache-Control': 'no-cache' },
      signal: controller.signal,
    });

    if (!response.ok) {
      return null;
    }

    const text = await response.text();
    return text || null;
  } catch (error) {
    return null;
  } finally {
    window.clearTimeout(timeoutId);
  }
};

export default function useDeploymentRefresh() {
  const currentSignature = useRef(null);

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      return undefined;
    }

    let shouldRun = true;
    const intervalMs = Number(process.env.REACT_APP_VERSION_POLL_MS) || DEFAULT_POLL_INTERVAL;

    const checkForUpdates = async () => {
      if (document.visibilityState === 'hidden') {
        return;
      }
      const signature = await fetchManifestSignature();
      if (!shouldRun || !signature) {
        return;
      }

      if (currentSignature.current && currentSignature.current !== signature) {
        window.location.reload();
        return;
      }

      currentSignature.current = signature;
    };

    checkForUpdates();
    const intervalId = window.setInterval(checkForUpdates, intervalMs);
    window.addEventListener('online', checkForUpdates);
    document.addEventListener('visibilitychange', checkForUpdates);

    return () => {
      shouldRun = false;
      window.clearInterval(intervalId);
      window.removeEventListener('online', checkForUpdates);
      document.removeEventListener('visibilitychange', checkForUpdates);
    };
  }, []);
}
